import { CheckCircle2, Info, MinusCircle } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useCsmDirectory, type CsmDirectoryEntry } from "../api";
import { RenewalsSubNav } from "../components/SubNav";

function fmtDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Renewals → CSMs. A read-only directory of the HubSpot CSM roster that feeds
 * the pipeline's CSM suggestions, plus an explainer of where the list comes
 * from so admins can add/assign people in HubSpot and have it flow through.
 */
export function CsmsPage() {
  const dir = useCsmDirectory();
  const entries = dir.data?.entries ?? [];
  const assignable = entries.filter((e) => e.has_login).length;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Renewals — CSMs</h1>
        <p className="text-sm text-muted-foreground">
          The HubSpot Customer Success roster that powers CSM suggestions on the
          pipeline.
        </p>
      </div>
      <RenewalsSubNav />

      {/* Where the list comes from — the answer to "how do I add a CSM?" */}
      <Card className="border-brand/30 bg-brand-light/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Info className="h-4 w-4 text-brand" />
            Where this list comes from
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-foreground/80">
          <p>
            This directory is the <strong>HubSpot owner roster</strong> — everyone
            on the HubSpot <em>Customer Success</em> team, plus any owner assigned
            as the <strong>CSM</strong> on a HubSpot company. It syncs from HubSpot
            into Snowflake and refreshes on the daily 8:00 AM job (or whenever an
            admin clicks <strong>Run refresh</strong> on the pipeline).
          </p>
          <p>
            <strong>To add or change who manages an account:</strong> set the CSM
            on that company in HubSpot. On the next sync they appear here and
            become the <em>suggested</em> CSM for that company's accounts in the
            pipeline (shown as <span className="italic">?Name</span>).
          </p>
          <p>
            For someone to be <strong>assignable</strong> here — a hard CSM
            assignment, and the ability to edit renewals themselves — they also
            need to <strong>sign into cinch ops once</strong>. That creates their
            account (matched by email); until then their name shows only as a
            suggestion. Accounts can also be assigned to a{" "}
            <strong>partner</strong> instead of an internal CSM directly on the
            pipeline.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>CSM directory</CardTitle>
          <CardDescription>
            {dir.data
              ? `${entries.length} HubSpot owner${entries.length === 1 ? "" : "s"} · ${assignable} signed into cinch ops · synced ${fmtDateTime(dir.data.synced_at)}`
              : "Loading…"}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {dir.isLoading && (
            <p className="px-4 py-3 text-sm text-muted-foreground">Loading…</p>
          )}
          {dir.error && (
            <p className="px-4 py-3 text-sm text-destructive">
              Couldn't load the CSM directory.
            </p>
          )}
          {dir.data && entries.length === 0 && (
            <p className="px-4 py-3 text-sm text-muted-foreground">
              No CSMs synced yet. Run a refresh on the pipeline, or assign a CSM
              to a company in HubSpot.
            </p>
          )}
          {dir.data && entries.length > 0 && <CsmTable entries={entries} />}
        </CardContent>
      </Card>
    </div>
  );
}

function CsmTable({ entries }: { entries: CsmDirectoryEntry[] }) {
  return (
    <Table containerClassName="max-h-[calc(100vh-26rem)]">
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Team</TableHead>
          <TableHead>cinch ops login</TableHead>
          <TableHead align="right">Assigned</TableHead>
          <TableHead>HubSpot owner</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody zebra>
        {entries.map((e) => (
          <TableRow key={e.hubspot_owner_id}>
            <TableCell className="font-medium">{e.display_name}</TableCell>
            <TableCell className="text-muted-foreground">{e.email ?? "—"}</TableCell>
            <TableCell>
              {e.is_cs_team ? (
                <Badge variant="info">CS team</Badge>
              ) : (
                <span className="text-xs text-muted-foreground">Company CSM</span>
              )}
            </TableCell>
            <TableCell>
              {e.has_login ? (
                <span
                  className="inline-flex items-center gap-1 text-success"
                  title={
                    e.last_login
                      ? `Last login ${fmtDateTime(e.last_login)}`
                      : "Has signed in"
                  }
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Assignable
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1 text-muted-foreground"
                  title="Hasn't signed into cinch ops — shows as a suggestion only"
                >
                  <MinusCircle className="h-3.5 w-3.5" />
                  Suggestion only
                </span>
              )}
            </TableCell>
            <TableCell numeric>
              {e.assigned_count > 0 ? e.assigned_count : "—"}
            </TableCell>
            <TableCell className="font-mono text-xs text-muted-foreground">
              {e.hubspot_owner_id}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
